// @lat: [[tasks#SEO Tasks#Frontend#Sitemap plan]]
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Map as MapIcon, Plus, RefreshCw, Loader2, ArrowRight } from "lucide-react";
import * as api from "../../lib/api";
import { analyzeSupport, buildSitemapPlan, PLAN_KINDS } from "../../lib/sitemapSupport";
import { TIERS } from "../../lib/queryMapPlan";
import { PRIMARY, PriorityLabel } from "./shared";

const BULK = 5;
const PAGE = 20;

const KIND_COLOR = {
  old: "#B91C1C", pillar_link: "#2563EB", hub: "#1D4ED8", links: "#2563EB", pillar_write: "#DC2626", write: "#DC2626",
};

export function KindChip({ kind }) {
  const color = KIND_COLOR[kind];
  return (
    <span
      className="inline-flex items-center px-1.5 py-px rounded text-[10px] font-bold whitespace-nowrap"
      style={{ color, backgroundColor: `${color}14` }}
    >
      {PLAN_KINDS[kind].label}
    </span>
  );
}

function TierChip({ tier }) {
  const t = TIERS[tier] || TIERS.unscored;
  return (
    <span
      className="inline-flex items-center px-1.5 py-px rounded text-[10px] font-bold whitespace-nowrap border border-zinc-200"
      style={{ backgroundColor: t.bg, color: t.text }}
    >
      {t.label}
    </span>
  );
}

/** Loads what the Visual Sitemap analyses and builds its plan. Shared by this tab and the sitemap. */
export function useSitemapPlan() {
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const [tree, c, t] = await Promise.all([
        api.getSitemapTree(),
        api.getClusters().catch(() => ({ clusters: [] })),
        api.getQueryTopics({ limit: 200 }).catch(() => ({ topics: [] })),
      ]);
      if (tree.needs_migration) {
        setPlan({ items: [], insights: null, scored: false });
        return;
      }
      const labels = new Map((c.clusters || []).map((cl) => [String(cl.cluster_id), cl.label]));
      setPlan(buildSitemapPlan(tree, analyzeSupport(tree, labels), t.topics || []));
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load the sitemap.");
      setPlan({ items: [], insights: null, scored: false });
    }
  }, []);
  useEffect(() => { load(); }, [load]);
  return { plan, error, setError, load };
}

/** Matches recommendations to tasks by title, so one already created shows its key instead. */
export function useExistingTasks(tasks) {
  return useMemo(() => {
    const m = new Map();
    (tasks || []).filter((t) => t.status !== "canceled").forEach((t) => m.set(t.title.trim().toLowerCase(), t));
    return (item) => m.get(item.task.title.trim().toLowerCase());
  }, [tasks]);
}

export function CreateButton({ item, task, busy, onCreate }) {
  if (task) {
    return (
      <Link
        to={`/dashboard/tasks/${task.key}`}
        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-lg border border-zinc-200 text-zinc-500 hover:bg-zinc-50"
      >
        {task.key} <ArrowRight size={12} />
      </Link>
    );
  }
  return (
    <button
      onClick={() => onCreate([item])}
      disabled={busy}
      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-white rounded-lg disabled:opacity-40 whitespace-nowrap"
      style={{ backgroundColor: PRIMARY }}
    >
      {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Create task
    </button>
  );
}

/** Creates tasks one by one; `busy` holds the item ids in flight. */
export function useCreateTasks(onCreated, setError) {
  const [busy, setBusy] = useState(new Set());
  const create = async (items) => {
    setBusy((b) => new Set([...b, ...items.map((i) => i.id)]));
    const made = [];
    try {
      for (const item of items) made.push(await api.createTask(item.task));
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to create task.");
    } finally {
      setBusy((b) => new Set([...b].filter((id) => !items.some((i) => i.id === id))));
      if (made.length) onCreated?.(made);
    }
  };
  return { busy, create };
}

function Insight({ n, label, bad }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-[11px] ${bad && n ? "bg-red-50 text-red-700" : "bg-zinc-100 text-zinc-600"}`}>
      <span className="font-bold tabular-nums">{n}</span> {label}
    </span>
  );
}

/** Ranked tasks from the Visual Sitemap's support analysis: hub, pillar and post links per commercial page. */
export default function SitemapPlanTab({ tasks, onCount, onCreated }) {
  const { plan, error, setError, load } = useSitemapPlan();
  const existing = useExistingTasks(tasks);
  const { busy, create } = useCreateTasks(onCreated, setError);
  const [shown, setShown] = useState(PAGE);
  const open = plan ? plan.items.filter((i) => !existing(i)) : [];

  useEffect(() => { onCount?.(plan ? open.length : null); }, [plan, open.length, onCount]);

  if (!plan) {
    return <div className="py-10 text-center text-xs text-zinc-400">Loading…</div>;
  }
  const ins = plan.insights;

  return (
    <div className="space-y-3">
      {error && (
        <div className="px-4 py-2.5 bg-red-50 border border-red-100 rounded-xl text-xs text-red-600">{error}</div>
      )}

      <div className="bg-white rounded-xl border border-zinc-100 shadow-sm px-5 py-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <MapIcon size={14} className="text-zinc-900" />
              <p className="text-sm font-bold text-zinc-900">
                {open.length ? `${open.length} recommended task${open.length === 1 ? "" : "s"}` : "Every sitemap recommendation has a task"}
              </p>
            </div>
            <p className="text-xs text-zinc-500 mt-1">
              What each commercial page is missing: a hub page above it, a link from its cluster's pillar, and 3+ supporting
              posts. One task per fix, grouped when one hub or pillar helps many pages.
              {" "}{plan.scored
                ? "Ranked by the lead value of the pages helped, then by how cheap the fix is."
                : "No search topics are scored yet, so fixes that help more pages rank first."}
            </p>
            {ins && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                <Insight n={ins.none} label={`of ${ins.pages} pages with no support`} bad />
                <Insight n={ins.strong} label="fully supported" />
                <Insight n={ins.hubsMissing} label="sections without a hub" bad />
                <Insight n={ins.clustersNoPillar} label={`of ${ins.clusters} clusters without a pillar`} bad />
                {ins.old > 0 && <Insight n={ins.old} label="old copies live" bad />}
                {ins.utility > 0 && <Insight n={ins.utility} label="utility pages skipped" />}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} title="Refresh" className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-50">
              <RefreshCw size={13} />
            </button>
            <Link
              to="/dashboard/sitemap"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-lg border border-zinc-200 text-zinc-700 hover:bg-zinc-50"
            >
              Open sitemap <ArrowRight size={12} />
            </Link>
            {open.length > 0 && (
              <button
                onClick={() => create(open.slice(0, BULK))}
                disabled={busy.size > 0}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-white rounded-lg disabled:opacity-40"
                style={{ backgroundColor: PRIMARY }}
              >
                <Plus size={12} /> Create top {Math.min(BULK, open.length)}
              </button>
            )}
          </div>
        </div>
      </div>

      {plan.items.length === 0 && (
        <div className="py-10 text-center text-xs text-zinc-400">
          Nothing to act on. Crawl your site on the Visual Sitemap first, or every commercial page is already supported.
        </div>
      )}

      {plan.items.slice(0, shown).map((item, i) => {
        const task = existing(item);
        return (
          <div key={item.id} className="flex items-start gap-4 bg-white rounded-xl border border-zinc-100 shadow-sm px-5 py-3.5">
            <span className="w-5 pt-0.5 text-right text-xs font-bold text-zinc-300 tabular-nums">{i + 1}</span>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-1.5 mb-1">
                <KindChip kind={item.kind} />
                {plan.scored && item.kind !== "old" && <TierChip tier={item.tier} />}
                <PriorityLabel priority={item.task.priority} />
              </div>
              <p className={`text-sm font-bold ${task ? "text-zinc-400" : "text-zinc-900"}`}>{item.label}</p>
              <p className="text-xs text-zinc-500 mt-0.5">{item.why}</p>
              {item.helped.length > 1 && (
                <p className="text-[11px] text-zinc-400 mt-0.5 truncate" title={item.helped.map((p) => p.title).join(", ")}>
                  Helps {item.helped.map((p) => p.title).join(", ")}
                </p>
              )}
            </div>
            <div className="flex-shrink-0">
              <CreateButton item={item} task={task} busy={busy.has(item.id)} onCreate={create} />
            </div>
          </div>
        );
      })}

      {plan.items.length > shown && (
        <button onClick={() => setShown((n) => n + PAGE)} className="w-full py-2 text-xs font-bold text-zinc-500 hover:text-zinc-800">
          Show {Math.min(PAGE, plan.items.length - shown)} more of {plan.items.length - shown}
        </button>
      )}
    </div>
  );
}
